import React from "react";

import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FormAuthoringCoachPopup } from "./FormAuthoringCoachPopup";

import { useAskPyTriggerVisible } from "../hooks/useAskPyTriggerVisible";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type AskPyNavTriggerButtonProps = {
	onClick: () => void;
	className?: string;
	expanded?: boolean;
};

/** Presentational trigger button — kept pure so Storybook/judge fixtures can render it without store or router context. */
export const AskPyNavTriggerButton: React.FC<AskPyNavTriggerButtonProps> = ({ onClick, className, expanded }) => (
	<button
		type="button"
		onClick={onClick}
		data-testid="ask-py-nav-trigger"
		aria-label="Ask PerformYard"
		aria-expanded={expanded}
		// primary-9 (#031a38) on the default white bar. TopNav only defines
		// --pyo-topnav-action-color on a bar it has painted with the side-nav theme, where the
		// theme foreground is what stays readable. Hover mirrors the nav-item treatment: an 8%
		// blend of the foreground over the bar, so it darkens a light bar and lightens a dark one.
		className={`flex cursor-pointer items-center gap-1.5 rounded border-none bg-transparent px-2 py-1.5 font-[inherit] text-sm font-semibold text-[color:var(--pyo-topnav-action-color,#031a38)] transition-colors hover:bg-[color-mix(in_srgb,currentColor_8%,transparent)] ${className || ""}`}
	>
		<FontAwesomeIcon icon={faSparkles} className="text-base" />
		<span className="whitespace-nowrap">Ask PerformYard</span>
	</button>
);

/** Self-gating top-nav trigger: mounts in both navs, opens/closes the docked panel. */
export const AskPyNavTrigger: React.FC = () => {
	const visible = useAskPyTriggerVisible();
	const isPanelOpen = useAskPerformYardStore((state) => state.isPanelOpen);
	const openPanel = useAskPerformYardStore((state) => state.openPanel);
	const closePanel = useAskPerformYardStore((state) => state.closePanel);

	if (!visible) return null;

	// Positioning context for the coach callout, which hangs below the trigger it points at.
	return (
		<div className="relative flex items-center">
			<AskPyNavTriggerButton onClick={isPanelOpen ? closePanel : openPanel} expanded={isPanelOpen} />
			<FormAuthoringCoachPopup />
		</div>
	);
};

export default AskPyNavTrigger;
